// concepts/c5/home.js — Concept 5 "Synthesis" HOMEPAGE-ONLY behaviour
// ============================================================
// Adapted from concepts/c2/script.js's proven scroll-story engine:
// progress is always derived live from getBoundingClientRect() (never
// cached offsets), so it stays correct across resizes with zero
// recomputation bookkeeping, and only opacity/transform are touched every
// frame, so there is no layout thrash. Extended here with:
//   1. revision 1 (spec item 1, critical fix): a "hero" pre-pin beat on the
//      SAME phone element the pinned story uses (see updateStory below) --
//      there is no separate hero-only phone anymore, so the two-phones/
//      hero-collision bug this replaces is structurally impossible now.
//   2. the beat-04 UNDERSTAND annotation break-out, driven by the same
//      activeBeat state the beat engine already tracks, using CSS
//      *transitions* (not one-shot animations) so it is fully reversible
//      when the user scrolls back up — the brief's "clean exit both
//      scroll directions" requirement.
//   3. round 11 (owner design): the beat 00/01 fusion. A single shared
//      chart panel now answers to EITHER beat via `data-beats="0 1"`
//      (space-separated -- every other beat keeps the original singular
//      `data-beat`), and additionally gets a `.is-meet` mode class exactly
//      when beat "1" is active. The same transition also drives the beat-0
//      decision ticker and the chart's crossfade between hand-drawn outcome
//      variants -- both start the instant beat 0 activates and stop the
//      instant it doesn't, so there is never a dangling interval on a beat
//      that isn't showing.
//   4. round 12 (owner: "way too fast" -- three independently staggered
//      ~1.1s intervals read as jittery, not like a believable stream of
//      decisions). The ticker is now ONE setInterval swapping all three
//      tokens together as a single "configuration" every ~1.8s, and the
//      chart crossfade fires once per configuration swap, synced to it --
//      see swapAllTokens/startTicker below.

(() => {
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 900px)');

  const story = document.querySelector('.story');
  const revealEls = Array.from(document.querySelectorAll('[data-reveal]'));

  let engineActive = false;
  let ticking = false;

  // ---------- story beat engine (single story section, 6 beats) ----------
  // Owner direction 2026-07-14: the engine now drives BOTH the desktop
  // phone stage and the mobile pinned card (.m-stage) -- their beat
  // screens/copies/dots share data-beat keys, and only one variant is
  // visible at a time, so toggling classes on both is free.
  const state = story ? {
    // Matches both the ordinary singular `data-beat` screens (1-5) and the
    // shared beat 00/01 chart panel's `data-beats="0 1"` list.
    beatScreens: Array.from(story.querySelectorAll('.beat-screen[data-beat], .beat-screen[data-beats]')),
    beatCopies: Array.from(story.querySelectorAll('.beat-copy[data-beat]')),
    dots: Array.from(story.querySelectorAll('.beat-dots i[data-beat]')),
    caption: story.querySelector('.story-desktop .phone-caption'),
    breakoutStage: story.querySelector('.story-desktop .breakout-stage'),
    breakoutBeat: story.querySelector('.story-desktop .breakout-stage') ? story.querySelector('.story-desktop .breakout-stage').dataset.breakoutBeat : null,
    activeBeat: null,
    keys: null,
  } : null;

  function setActiveBeat(beatKey) {
    if (!state || state.activeBeat === beatKey) return;
    state.activeBeat = beatKey;
    for (const el of state.beatScreens) {
      let isActive;
      if (el.dataset.beats) {
        // The shared chart panel: active on either listed beat, and in
        // "meet myoxin" mode specifically on beat 1.
        isActive = el.dataset.beats.split(/\s+/).includes(beatKey);
        el.classList.toggle('is-meet', beatKey === '1');
      } else {
        isActive = el.dataset.beat === beatKey;
      }
      el.classList.toggle('is-active', isActive);
      // hasAttribute (not the dataset value) decides whether this beat
      // drives the caption slot at all -- beat 00 carries an EMPTY
      // data-caption on purpose (it has no caption yet, pending its video
      // asset), so it must still take over the slot and blank it, not
      // silently leave the previous beat's text on screen.
      if (isActive && state.caption && el.hasAttribute('data-caption')) {
        const captionText = el.dataset.caption || '';
        state.caption.textContent = captionText;
        state.caption.style.visibility = captionText ? '' : 'hidden';
      }
    }
    for (const el of state.beatCopies) {
      el.classList.toggle('is-active', el.dataset.beat === beatKey);
    }
    for (const el of state.dots) {
      el.classList.toggle('is-active', el.dataset.beat === beatKey);
    }
    if (state.breakoutStage) {
      state.breakoutStage.classList.toggle('is-active', beatKey === state.breakoutBeat);
    }

    // Beat 0 decision ticker + chart crossfade: alive only while beat 0 is
    // showing. Entering beat 1 also snaps the chart to its frozen "meet
    // myoxin" state (see freezeChartsForMeet below).
    if (beatKey === '0') {
      startTicker();
    } else {
      stopTicker();
      if (beatKey === '1') freezeChartsForMeet();
    }
  }

  function updateStory() {
    if (!state || !state.beatScreens.length) return;
    const vh = window.innerHeight;
    const rect = story.getBoundingClientRect();

    // Pre-pin: the sticky box hasn't reached top:0 yet (rect.top > 0 means
    // .story's own top edge -- and therefore the sticky box's static
    // position -- is still below the viewport top), so the phone/card is
    // still rising through normal document flow, not yet pinned. Owner
    // correction 2026-07-14: hold beat "0" (the pain screen) -- the peek
    // shows screen zero from the start and nothing changes until the
    // element is fully in view and pinned scrolling actually begins.
    if (rect.top > 0) {
      setActiveBeat('0');
      return;
    }

    const range = story.offsetHeight - vh;
    let progress;
    if (range <= 0) {
      progress = rect.top <= 0 ? 1 : 0;
    } else {
      progress = (-rect.top) / range;
    }
    progress = Math.min(1, Math.max(0, progress));

    // Derived from the beat-dots (always one singular data-beat per beat,
    // 0-5), not beatScreens -- the shared chart panel's data-beats="0 1"
    // would otherwise collapse two distinct beats into one dot's worth of
    // scroll progress.
    const keys = state.keys || (state.keys = Array.from(new Set(state.dots.map((el) => el.dataset.beat))));
    const n = keys.length;
    let idx = Math.floor(progress * n);
    if (idx >= n) idx = n - 1;
    if (idx < 0) idx = 0;

    setActiveBeat(keys[idx]);
  }

  // ---------- beat 0 decision ticker + chart crossfade (round 11, pacing
  // fixed round 12) ----------
  // Every chart-panel instance (desktop .phone-mount + mobile .m-screen)
  // gets its own pair of overlaid <path> elements; both instances are
  // driven from the same timer so they always show the same variant/token
  // configuration at once. Round 12 (owner: the old three-independent-
  // staggered-intervals version read as "way too fast"): all three tokens
  // now swap together as a single "configuration" on ONE setInterval, held
  // for ~1.8s before the next swap, and the chart crossfades exactly once
  // per configuration change -- see swapAllTokens below.
  const TICKER_TOKENS = {
    freq: ['1x', '3x', '2x'],
    sets: ['2', '5', '3'],
    failure: ['to', 'far from', 'close to'],
  };
  // Round 13 (owner: "guessing sometimes works, often doesn't" -- the
  // whole set should read as unspectacular, not as four different flavors
  // of success). All four now wobble in a tight band either side of
  // index.html's zero baseline (.gains-baseline, y=470) instead of the
  // round-12 set, which mostly just rocketed upward at different speeds.
  // Index 0 (modest, steady) is also the variant baked into the HTML's
  // default `.is-shown` path -- still the best of the four, still clearly
  // unspectacular. Index 3 (small early gain, then a flat, low plateau) is
  // the new freeze target for beat 1 -- MEDIOCRE_VARIANT below -- so ON
  // YOUR OWN reads as "some progress, clearly not much," not as the
  // rosiest guess of the set. Coordinates match the round-12 taller
  // viewBox (320x500) -- see index.html's chart-panel markup for the
  // matching frozen/myoxin lines and the zero baseline itself.
  const CHART_VARIANTS = [
    'M14,328 C34,318 50,332 66,308 C84,286 98,302 116,274 C136,248 152,264 172,230 C192,202 210,218 232,194 C254,174 272,190 290,170 C300,160 304,192 308,186', // 1: modest, steady climb -- the best of the four, still unspectacular
    'M14,328 C32,320 48,338 64,318 C82,302 96,320 114,300 C134,284 150,300 170,282 C190,266 208,284 228,262 C248,246 266,262 288,250 C296,244 302,258 308,248', // 2: hovers around zero with wobble, dips slightly below by the end
    'M14,330 C30,342 44,354 60,348 C78,342 92,358 110,350 C130,340 146,354 166,340 C186,330 202,344 222,332 C242,322 260,334 280,324 C292,316 300,330 308,320', // 3: dips below zero early, creeps back to barely above by the end
    'M14,328 C32,314 46,304 62,296 C80,288 94,306 112,296 C132,286 148,302 166,290 C186,280 202,298 222,288 C242,278 258,294 278,286 C292,280 300,294 308,288', // 4: small early gain, then a flat, low plateau -- the freeze target
  ];
  // Round 13 (owner: "freeze on mediocre, not best"): beat 1 used to snap
  // back to index 0, the best-looking guess of the four -- that undersold
  // the contrast. It now snaps to index 3 instead. See freezeChartsForMeet
  // below and index.html's matching frozen path + label position.
  const MEDIOCRE_VARIANT = CHART_VARIANTS[3];

  const chartPanelState = Array.from(document.querySelectorAll('.chart-panel')).map((panel) => ({
    pathA: panel.querySelector('.user-line-a'),
    pathB: panel.querySelector('.user-line-b'),
    showingA: true,
    cursor: 0,
  }));
  const tokenIndex = { freq: 0, sets: 0, failure: 0 };
  const tokenEls = {
    freq: Array.from(document.querySelectorAll('.ticker-token[data-token="freq"]')),
    sets: Array.from(document.querySelectorAll('.ticker-token[data-token="sets"]')),
    failure: Array.from(document.querySelectorAll('.ticker-token[data-token="failure"]')),
  };
  let tickerTimers = [];

  function crossfadeChart() {
    for (const p of chartPanelState) {
      if (!p.pathA || !p.pathB) continue;
      p.cursor = (p.cursor + 1) % CHART_VARIANTS.length;
      const nextD = CHART_VARIANTS[p.cursor];
      const showEl = p.showingA ? p.pathB : p.pathA;
      const hideEl = p.showingA ? p.pathA : p.pathB;
      showEl.setAttribute('d', nextD);
      hideEl.classList.remove('is-shown');
      showEl.classList.add('is-shown');
      p.showingA = !p.showingA;
    }
  }

  // One configuration swap: all three tokens change together, then the
  // chart crossfades once -- synced, not per-token.
  function swapAllTokens() {
    for (const key of ['freq', 'sets', 'failure']) {
      const values = TICKER_TOKENS[key];
      tokenIndex[key] = (tokenIndex[key] + 1) % values.length;
      const nextValue = values[tokenIndex[key]];
      for (const el of tokenEls[key]) {
        el.textContent = nextValue;
        el.classList.remove('is-flash');
        void el.offsetWidth; // restart the flash animation on repeat swaps
        el.classList.add('is-flash');
      }
    }
    crossfadeChart();
  }

  function startTicker() {
    if (tickerTimers.length || reduceMotionQuery.matches) return;
    // setInterval fires only after its delay, so the initial configuration
    // holds its full ~1.8s before the first swap -- no instant jump the
    // moment beat 0 becomes active.
    const intervalId = window.setInterval(swapAllTokens, 1800);
    tickerTimers.push(intervalId);
  }

  function stopTicker() {
    for (const id of tickerTimers) {
      window.clearInterval(id);
    }
    tickerTimers = [];
  }

  // Beat 1 payoff: whichever variant was showing, snap it to the mediocre
  // plateau line (round 13: freeze on mediocre, not best) and make sure
  // it's the one visible -- deterministic every time beat 1 activates,
  // from either scroll direction.
  function freezeChartsForMeet() {
    for (const p of chartPanelState) {
      if (!p.pathA) continue;
      p.pathA.setAttribute('d', MEDIOCRE_VARIANT);
      p.pathA.classList.add('is-shown');
      if (p.pathB) p.pathB.classList.remove('is-shown');
      p.showingA = true;
      p.cursor = 0;
    }
  }

  function onFrame() {
    ticking = false;
    if (engineActive) updateStory();
  }

  function requestTick() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(onFrame);
  }

  // ---------- reveal-on-scroll (belief, intelligence, access, final) ----------
  let revealObserver = null;
  function setupRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return;
    revealObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          revealObserver.unobserve(entry.target);
        }
      }
    }, { threshold: 0.25, rootMargin: '0px 0px -8% 0px' });
    for (const el of revealEls) revealObserver.observe(el);
  }
  function showAllReveals() {
    for (const el of revealEls) el.classList.add('is-in');
  }

  // ---------- persistent floating store badges (owner addition, revision 1c; round 3 fix, spec item 6) ----------
  // Desktop-only download affordance now that the header is static and
  // scrolls away. Round 3 fix: the floats used to only watch the hero's
  // own store-row, so they kept showing even once the final CTA section's
  // real store-row scrolled into view -- a visible duplicate of the same
  // two buttons. Now BOTH rows are observed; the floats show only when
  // NEITHER real row is on screen AND the hero row has already been
  // scrolled above (the original "have we passed the hero" rule, still
  // needed so the floats don't show before the hero is even reached).
  // Both observer entries feed one combined recompute on every callback,
  // so it stays flicker-free regardless of which boundary fired.
  const heroStoreRow = document.querySelector('.hero-store-row');
  const finalStoreRow = document.querySelector('.final .store-row');
  const floatStore = document.querySelector('[data-float-store]');
  if (heroStoreRow && finalStoreRow && floatStore && 'IntersectionObserver' in window) {
    const rowState = {
      heroIntersecting: true,
      heroAboveViewport: false,
      finalIntersecting: false,
    };
    function updateFloatVisibility() {
      const shouldShow = rowState.heroAboveViewport && !rowState.heroIntersecting && !rowState.finalIntersecting;
      floatStore.classList.toggle('is-visible', shouldShow);
    }
    const floatStoreObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === heroStoreRow) {
          rowState.heroIntersecting = entry.isIntersecting;
          rowState.heroAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        } else if (entry.target === finalStoreRow) {
          rowState.finalIntersecting = entry.isIntersecting;
        }
      }
      updateFloatVisibility();
    }, { threshold: 0 });
    floatStoreObserver.observe(heroStoreRow);
    floatStoreObserver.observe(finalStoreRow);
  }

  // ---------- engine gate: motion allowed (desktop AND mobile pinned card) ----------
  function syncEngineState() {
    const shouldRun = !reduceMotionQuery.matches;
    if (shouldRun && !engineActive) {
      engineActive = true;
      if (state) state.keys = null;
      requestTick();
    } else if (!shouldRun && engineActive) {
      engineActive = false;
      if (state) setActiveBeat(null);
    }
  }

  function init() {
    if (reduceMotionQuery.matches) {
      showAllReveals();
    } else {
      setupRevealObserver();
    }

    syncEngineState();
    if (engineActive) updateStory();

    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', () => { syncEngineState(); requestTick(); });

    if (reduceMotionQuery.addEventListener) {
      reduceMotionQuery.addEventListener('change', () => {
        syncEngineState();
        if (reduceMotionQuery.matches) showAllReveals();
      });
      mobileQuery.addEventListener('change', syncEngineState);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
